import type React from 'react';
import type { RelationshipT } from '../../../modernization-common/relationships';
import { DIRECTIONALITY } from '../../../modernization-common/relationships';
import { getDimensionsOfAnyShape } from '../components/annotations/utils';
import type { LineStyleT } from '../constants/relationship-renderer';
import { DEFAULT_EDGE_COLOR, LABEL_FONT_SIZE, LABEL_PADDING, LINE_STYLE } from '../constants/relationship-renderer';
import { CSS_VARIABLE_STAGE_ROTATION, CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../constants/stage-css-variables';
import type { AnnotationT } from '../types/annotations.type';
import type { EdgeDataT } from '../types/relationship-renderer.type';
export const getLabelDimensions = (name: string) => ({
  width: name.length * (LABEL_FONT_SIZE * 0.6) + LABEL_PADDING * 2,
  height: LABEL_FONT_SIZE + LABEL_PADDING * 2
});
export const getAnnotationCenter = (annotation: AnnotationT | undefined): [number, number] | null => {
  if (!annotation) return null;
  const dims = getDimensionsOfAnyShape(annotation.data, annotation.annotationType);
  if (!dims) return null;
  return [dims.x + dims.width / 2, dims.y + dims.height / 2];
};
export const getRelationshipEdgeColor = (annotation: AnnotationT | undefined, fallbackColor: string = DEFAULT_EDGE_COLOR): string => {
  return annotation?.annotationLabel?.style?.strokeColor ?? fallbackColor;
};
export const getRelationshipEdgeTextColor = (annotation: AnnotationT | undefined): string | undefined => {
  return annotation?.annotationLabel?.style?.fillColor;
};
export const getCounterScaleStyle = (originX: number, originY: number): React.CSSProperties => ({
  transformOrigin: `${originX}px ${originY}px`,
  transform: `rotate(calc(-1 * var(${CSS_VARIABLE_STAGE_ROTATION}))) scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`
});
export const getCounterScaleOnlyStyle = (originX: number, originY: number): React.CSSProperties => ({
  transformOrigin: `${originX}px ${originY}px`,
  transform: `scale(var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE}))`
});
export const getMidpoint = (source: [number, number], target: [number, number]): [number, number] => [(source[0] + target[0]) / 2, (source[1] + target[1]) / 2];
export const buildEdgesFromRelationships = (visibleRelationships: [string, RelationshipT][], annotationStore: Record<string, AnnotationT>, fallbackColor?: string, lineStyle: LineStyleT = LINE_STYLE.SOLID): EdgeDataT[] => {
  if (!visibleRelationships || visibleRelationships.length === 0) return [];
  return Object.values(visibleRelationships.reduce<Record<string, EdgeDataT>>((acc, [id, rel]) => {
    const sourceCenter = getAnnotationCenter(annotationStore[rel.sourceAnnotationId]);
    const targetCenter = getAnnotationCenter(annotationStore[rel.targetAnnotationId]);
    if (!sourceCenter || !targetCenter) return acc;
    const pairKey = `${rel.sourceAnnotationId}::${rel.targetAnnotationId}`;
    const existing = acc[pairKey];
    if (existing) {
      existing.names.push(rel.relationshipLabel.relationshipLabelName);
    } else {
      acc[pairKey] = {
        id,
        sourceCenter,
        targetCenter,
        names: [rel.relationshipLabel.relationshipLabelName],
        color: getRelationshipEdgeColor(annotationStore[rel.sourceAnnotationId], fallbackColor),
        textColor: getRelationshipEdgeTextColor(annotationStore[rel.sourceAnnotationId]),
        directed: rel.relationshipLabel.directionality === DIRECTIONALITY.DIRECTED,
        lineStyle
      };
    }
    return acc;
  }, {}));
};
export const getArrowheadPoints = (target: [number, number], angle: number, arrowSize: number): string => {
  const [tipX, tipY] = target;
  const baseX = tipX - arrowSize * Math.cos(angle);
  const baseY = tipY - arrowSize * Math.sin(angle);
  const halfWidth = arrowSize / 2;
  const left = [baseX - halfWidth * Math.sin(angle), baseY + halfWidth * Math.cos(angle)];
  const right = [baseX + halfWidth * Math.sin(angle), baseY - halfWidth * Math.cos(angle)];
  return `${tipX},${tipY} ${left[0]},${left[1]} ${right[0]},${right[1]}`;
};